import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs'; // Import BehaviorSubject and Observable
import { map } from 'rxjs/operators';

// This interface is present in multiple files. Consider moving it to a shared 'types.ts' file.
// export interface LoginForm {
//     phone: string;
//     password: string;
// }

// // Define a more specific User interface if possible
// export interface AuthUser {
//     id: number; // Assuming user ID is a number
//     session_token: string;
//     is_verified: boolean;
//     // ... other user properties
// }

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    // BehaviorSubject to hold the current authentication status and user data
    // It will emit the current user object if logged in, null otherwise.
    private _currentUserSubject: BehaviorSubject<any | null>;
    public currentUser$: Observable<any | null>; // Public observable for other services/components to subscribe to

    // Public observable to check if a user is authenticated (has token and is verified)
    public isAuthenticated$: Observable<boolean>;

    constructor(private http: HttpClient) {
        // Initialize the subject with the user data from localStorage at startup
        const storedUser = this.getAuthUserFromStorage();
        this._currentUserSubject = new BehaviorSubject<any | null>(storedUser);
        this.currentUser$ = this._currentUserSubject.asObservable();

        // isAuthenticated$ combines token presence and verification status
        this.isAuthenticated$ = this.currentUser$.pipe(
            map(user => {
                const tokenExists = this.getAuthToken() !== null && this.getAuthToken() !== 'null';
                const isVerified = this.getAuthVerify() === 'true';
                // A user is truly authenticated if a token exists AND they are verified.
                // If the user object in the subject is null, it overrides other checks.
                return user !== null && tokenExists && isVerified;
            })
        );
    }

    // --- Private helper to get user from localStorage ---
    private getAuthUserFromStorage(): any | null {
        const user = window.localStorage.getItem('auth-user');
        return user ? JSON.parse(user) : null;
    }

    // --- Existing localStorage methods, updated to also manage BehaviorSubject ---

    public setAuthToken = (token: string) => {
        window.localStorage.setItem('auth-token', token);
        // Do NOT update _currentUserSubject here directly, as token alone doesn't mean full auth
    }

    public getAuthToken = (): string | null => {
        return window.localStorage.getItem('auth-token');
    }

    public setAuthUser = (user: any | null) => { // User type can be made more specific
        if (user) {
            window.localStorage.setItem('auth-user', JSON.stringify(user));
            this._currentUserSubject.next(user); // Emit the new user
        } else {
            window.localStorage.removeItem('auth-user');
            this._currentUserSubject.next(null); // Emit null on logout
        }
    }

    public getAuthUser = (): any | null => {
        // Now, this method directly exposes the current value of the BehaviorSubject
        // which should be kept in sync with localStorage by setAuthUser.
        // This avoids re-parsing localStorage on every call.
        return this._currentUserSubject.getValue();
    }

    public setAuthVerify = (status: string) => {
        window.localStorage.setItem('auth-verify', status);
        // Re-evaluate and emit authentication status when verification changes
        // This will trigger isAuthenticated$ to update
        this._currentUserSubject.next(this.getAuthUser()); // Re-emit current user to trigger pipe
    }

    public getAuthVerify = (): string | null => {
        return window.localStorage.getItem('auth-verify');
    }

    public logOutUser = () => {
        window.localStorage.removeItem('auth-token');
        window.localStorage.removeItem('auth-verify');
        this.setAuthUser(null); // This will clear 'auth-user' and emit null via the subject
        // If you had biometric, also remove:
        // window.localStorage.removeItem('auth-biometric');
    }

    /**
     * Fetches the current IP address from an external service.
     * @returns {Promise<any>} A promise that resolves with the IP address data.
     */
    public getIPAddress(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get("http://ip-api.com/json")
                .subscribe({
                    next: (res: any) => resolve(res),
                    error: (err) => reject(err) // Handle errors in the promise
                });
        });
    }
}